/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Checkbox, Table } from 'antd';
import { useMemo, useCallback } from 'react';

import { useMimicTranslation } from '@/hooks/useMimicTranslation';

import './PermissionsTable.css';

interface Permission {
  name: string;
  action: string;
}

interface PermissionGroupData {
  resource: string;
  displayName: string;
  permissions: Permission[];
}

interface PermissionsTableProps {
  permissionGroups: PermissionGroupData[];
  permissionState: Record<string, boolean>;
  onPermissionToggle: (permissionName: string, enabled: boolean) => void;
}

interface PermissionRow {
  key: string;
  displayName: string;
  permissions: Record<string, string>;
}

const ACTION_ORDER: Record<string, number> = {
  create: 0,
  read: 1,
  update: 2,
  delete: 3,
  write: 4,
};

function actionSortKey(action: string): number {
  return ACTION_ORDER[action] ?? 5;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function PermissionsTable({ permissionGroups, permissionState, onPermissionToggle }: PermissionsTableProps) {
  const { t } = useMimicTranslation('apiTokens');

  const actionColumns = useMemo(
    () =>
      Array.from(
        permissionGroups.reduce((actions, group) => {
          group.permissions.forEach((perm) => actions.add(perm.action));
          return actions;
        }, new Set<string>()),
      ).sort((a, b) => actionSortKey(a) - actionSortKey(b)),
    [permissionGroups],
  );

  const rows: PermissionRow[] = useMemo(
    () =>
      permissionGroups.map((group) => ({
        key: group.resource,
        displayName: group.displayName,
        permissions: group.permissions.reduce(
          (acc, perm) => {
            acc[perm.action] = perm.name;
            return acc;
          },
          {} as Record<string, string>,
        ),
      })),
    [permissionGroups],
  );

  const handleToggleAll = useCallback(
    (action: string) => {
      const applicableRows = rows.filter((row) => row.permissions[action]);
      const allOn = applicableRows.every((row) => permissionState[row.permissions[action]]);
      const newValue = !allOn;

      applicableRows.forEach((row) => {
        onPermissionToggle(row.permissions[action], newValue);
      });
    },
    [rows, permissionState, onPermissionToggle],
  );

  const columns = useMemo(() => {
    const resourceColumn = {
      title: t('permissionsSection.resourceColumn'),
      dataIndex: 'displayName',
      key: 'displayName',
      width: 180,
    };

    const actionCols = actionColumns.map((action) => {
      const applicableRows = rows.filter((row) => row.permissions[action]);
      const allOn =
        applicableRows.length > 0 && applicableRows.every((row) => permissionState[row.permissions[action]]);

      return {
        title: (
          <div
            className="column-header-toggle"
            role="button"
            tabIndex={0}
            onClick={() => handleToggleAll(action)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggleAll(action);
              }
            }}
          >
            <Checkbox checked={allOn} disabled={applicableRows.length === 0} />
            <span>{capitalize(action)}</span>
          </div>
        ),
        key: action,
        width: 100,
        align: 'center' as const,
        render: (_: unknown, record: PermissionRow) => {
          const permissionName = record.permissions[action];
          if (!permissionName) {
            return <span className="permission-unavailable">&#x2013;</span>;
          }
          return (
            <Checkbox
              data-testid={`permission-checkbox-${permissionName}`}
              checked={permissionState[permissionName] || false}
              onChange={(e) => onPermissionToggle(permissionName, e.target.checked)}
            />
          );
        },
      };
    });

    return [resourceColumn, ...actionCols];
  }, [actionColumns, rows, permissionState, onPermissionToggle, handleToggleAll, t]);

  return (
    <div className="permissions-table">
      <Table dataSource={rows} columns={columns} pagination={false} size="middle" />
    </div>
  );
}
